import { useCallback, useEffect, useRef } from "react";
import { useProjectDataStore } from "stores/projectStore";
import {
  useChangeProject,
  useFetchProject,
  useInitOpenMap,
} from "hooks/projectHooks";
import { getProjectId } from "utils";
import { toast } from "react-toastify";

export const useSyncProject = () => {
  const projectId = useProjectDataStore((state) => state.projectId);
  const setProjectId = useProjectDataStore((state) => state.setProjectId);
  const treeRef = useProjectDataStore((state) => state.treeRef);
  const setTreeRef = useProjectDataStore((state) => state.setTreeRef);
  const changeProject = useChangeProject();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initTreeRef = useRef<any>(null);

  const initOpenMap = useInitOpenMap();

  const refetch = useFetchProject({
    pause: false,
    onError: () => toast.error("Failed to fetch project"),
    onSuccess: ({ data }) => initOpenMap(data.project.projectNodes),
  });

  useOnFocus(() => projectId && refetch());

  // Sync projectId from URL with projectId in store
  useEffect(() => {
    const pid = getProjectId();
    if (projectId !== null) {
      if (pid && projectId !== pid) {
        setProjectId(pid);
      }
    }
    if (!treeRef) {
      setTreeRef(initTreeRef);
    }
  }, [projectId, setProjectId, setTreeRef, treeRef, initTreeRef]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const originalProjectId = url.searchParams.get("originalProjectId");
    const originalProjectName = url.searchParams.get("originalProjectName");
    if (originalProjectName) {
      url.searchParams.delete("originalProjectName");
    }
    if (originalProjectId) {
      url.searchParams.delete("originalProjectId");
    }
    window.history.pushState({}, "", url.toString());
    if (originalProjectId) {
      changeProject(originalProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useOnFocus = (callback: (args?: any) => void, duration: number = 0) => {
  const futureTimeRef = useRef<Date | null>(null);
  const handleFocusOrVisibilityChange = useCallback(() => {
    const now = new Date();
    if (document.visibilityState === "visible") {
      if (futureTimeRef.current !== null && now > futureTimeRef.current) {
        callback();
      }
      futureTimeRef.current = null;
    } else {
      futureTimeRef.current = new Date(now.getTime() + duration * 60 * 1000);
    }
  }, [callback, duration]);

  useEffect(() => {
    window.addEventListener("focus", handleFocusOrVisibilityChange);
    document.addEventListener(
      "visibilitychange",
      handleFocusOrVisibilityChange,
    );

    return () => {
      window.removeEventListener("focus", handleFocusOrVisibilityChange);
      document.removeEventListener(
        "visibilitychange",
        handleFocusOrVisibilityChange,
      );
    };
  }, [handleFocusOrVisibilityChange]);
};
